import { useCallback, useEffect, useState } from 'react';

interface Settled<T> {
  loader: unknown;
  version: number;
  data?: T;
  error?: unknown;
}

export interface AsyncData<T> {
  /** True while the result for the CURRENT loader/version has not arrived (even if `data` still shows older data). */
  loading: boolean;
  data: T | undefined;
  error: unknown;
  /** Re-runs the loader (e.g. after a 409 told us our copy is stale). */
  reload: () => void;
  /** Replaces the loaded data without a round trip (e.g. with a mutation response). */
  setData: (next: T) => void;
}

interface Options {
  /** While a new request is loading, keep returning the previous successful data (stale-while-revalidate) so the
   * UI that shows it (and any focused control inside it) is not unmounted. */
  keepPrevious?: boolean;
}

/**
 * Loads data when `loader` changes (callers pass a `useCallback`-stable function). "Loading" is DERIVED from
 * whether the settled result belongs to the current loader/version, so no state is set synchronously inside
 * the effect (react-hooks/set-state-in-effect) and a stale response can never be shown for a newer request.
 */
export function useAsyncData<T>(loader: (signal: AbortSignal) => Promise<T>, options: Options = {}): AsyncData<T> {
  const [version, setVersion] = useState(0);
  const [settled, setSettled] = useState<Settled<T> | null>(null);
  const keepPrevious = options.keepPrevious ?? false;

  useEffect(() => {
    const controller = new AbortController();
    loader(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setSettled({ loader, version, data });
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) setSettled({ loader, version, error });
      });
    return () => controller.abort();
  }, [loader, version]);

  const reload = useCallback(() => setVersion((v) => v + 1), []);
  const setData = useCallback((next: T) => setSettled({ loader, version, data: next }), [loader, version]);

  const current = settled !== null && settled.loader === loader && settled.version === version ? settled : null;
  const shown = current ?? (keepPrevious && settled?.error === undefined ? settled : null);
  return { loading: current === null, data: shown?.data, error: current?.error, reload, setData };
}
